/**
 * Backlog project picker for the project Settings page.
 *
 * Replaces the legacy "type a project key chip + 追加 button" flow.
 * After spaceUrl + secretName are saved, the user clicks
 * 「プロジェクト一覧を取得」 — a callable enumerates the API key's
 * accessible Backlog workspaces and we render checkboxes. Selection
 * still ends up as `string[]` of `projectKey` values, matching the
 * legacy `BacklogConfig.backlogProjectKeys` shape, so the persistence
 * layer is unchanged.
 *
 * Pure render: caller owns selection state, busy state, project list,
 * archived projects, and error state. This keeps the component
 * trivially testable via `react-dom/server` without jsdom.
 */
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from '../../lib/utils';
import type { BacklogProjectListItem } from '../../lib/adminCallables';

export interface BacklogProjectPickerProps {
  /** Projects fetched from Backlog. `null` = never fetched yet. */
  projects: BacklogProjectListItem[] | null;
  /** Currently selected projectKey set. */
  selectedKeys: ReadonlyArray<string>;
  /** Whether the fetch button is disabled (e.g. spaceUrl/secretName empty, busy). */
  fetchDisabled: boolean;
  /** True while the callable is in flight. */
  fetching: boolean;
  /** Last fetch error, if any. */
  fetchError: string | null;
  /** Called when the user clicks 「プロジェクト一覧を取得」. */
  onFetch: () => void;
  /** Called with the new selection set when the user toggles a checkbox. */
  onToggle: (projectKey: string, next: boolean) => void;
  /** Called when the user clicks 「手入力に切り替え」 fallback. */
  onSwitchToManual: () => void;
}

export function BacklogProjectPicker(props: BacklogProjectPickerProps) {
  const {
    projects,
    selectedKeys,
    fetchDisabled,
    fetching,
    fetchError,
    onFetch,
    onToggle,
    onSwitchToManual,
  } = props;
  const selected = new Set(selectedKeys);

  return (
    <div data-testid="backlog-project-picker">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          // whitespace-nowrap defeats the per-character wrap on Japanese
          // labels when the parent is a tight flexbox; shrink-0 keeps
          // the button at its intrinsic width when the input next to it
          // tries to consume all the row.
          className="shrink-0 whitespace-nowrap"
          disabled={fetchDisabled || fetching}
          onClick={onFetch}
        >
          {fetching ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" />
              取得中…
            </span>
          ) : (
            'プロジェクト一覧を取得'
          )}
        </Button>
        <button
          type="button"
          className="text-xs text-slate-500 underline-offset-2 hover:underline"
          onClick={onSwitchToManual}
        >
          手入力に切り替え
        </button>
      </div>

      {fetchError ? (
        <p className="mt-2 text-xs text-red-700" role="alert">
          {fetchError}
        </p>
      ) : null}

      {projects && projects.length === 0 ? (
        <p className="mt-3 text-xs text-slate-500">
          このAPIキーでアクセス可能なプロジェクトはありませんでした。
        </p>
      ) : null}

      {projects && projects.length > 0 ? (
        <div className="mt-3">
          <p className="text-xs text-slate-500">
            連携するプロジェクトを選択してください（複数選択可）。
          </p>
          <ul className="mt-2 max-h-72 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
            {projects.map((p) => {
              const isChecked = selected.has(p.projectKey);
              return (
                <li
                  key={p.projectKey}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 text-sm',
                    p.archived ? 'bg-slate-50 text-slate-500' : null,
                  )}
                >
                  <label className="flex flex-1 items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => onToggle(p.projectKey, e.target.checked)}
                      aria-label={`${p.projectKey} ${p.name}`}
                    />
                    <span className="font-mono text-xs shrink-0 w-20 truncate">
                      {p.projectKey}
                    </span>
                    <span className="flex-1 truncate">{p.name}</span>
                    {p.archived ? (
                      <span
                        className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-600 shrink-0"
                        data-testid="archived-badge"
                      >
                        アーカイブ
                      </span>
                    ) : null}
                  </label>
                </li>
              );
            })}
          </ul>
          <p className="mt-1.5 text-[11px] text-slate-500">
            {selectedKeys.length} 件選択中
          </p>
        </div>
      ) : null}
    </div>
  );
}
